import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Button, Paper,
    FormControlLabel, Checkbox, Alert, Stepper, Step, StepLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    LinearProgress, Accordion, AccordionSummary, AccordionDetails, useTheme, alpha
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as TrashIcon,
    CheckCircle as CheckIcon,
    Shield as ShieldIcon,
    Visibility as VisibilityIcon,
    Timeline as TimelineIcon,
    Assessment as AssessmentIcon,
    Security as SecurityIcon,
    TrendingUp as TrendingIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function D7Prevention({ step, onStepUpdate, stepMapping, stepId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [activePhase, setActivePhase] = useState(0);

    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    const preventionPhases = [
        { name: 'Prevention Strategy', icon: <ShieldIcon />, color: '#2196f3' },
        { name: 'Detection Systems', icon: <VisibilityIcon />, color: '#ff9800' },
        { name: 'Implementation Timeline', icon: <TimelineIcon />, color: '#4caf50' },
        { name: 'Risk Mitigation', icon: <SecurityIcon />, color: '#9c27b0' },
        { name: 'Monitoring & Control', icon: <TrendingIcon />, color: '#f44336' },
        { name: 'Effectiveness Review', icon: <AssessmentIcon />, color: '#607d8b' }
    ];

    const addPreventionAction = () => {
        const newActions = [...(step.preventionActions || []), {
            id: Date.now(),
            title: '',
            description: '',
            phase: 'strategy',
            owner: '',
            department: '',
            startDate: '',
            targetDate: '',
            actualDate: '',
            status: 'planned',
            progress: 0,
            riskLevel: 'medium',
            effectiveness: ''
        }];
        updateStep({ preventionActions: newActions });
    };

    const updatePreventionAction = (index, field, value) => {
        const updatedActions = [...(step.preventionActions || [])];
        updatedActions[index] = { ...updatedActions[index], [field]: value };
        updateStep({ preventionActions: updatedActions });
    };

    const deletePreventionAction = (index) => {
        const updatedActions = (step.preventionActions || []).filter((_, i) => i !== index);
        updateStep({ preventionActions: updatedActions });
    };

    const addTimelineItem = () => {
        const newItems = [...(step.timelineItems || []), {
            id: Date.now(),
            task: '',
            description: '',
            owner: '',
            startDate: '',
            endDate: '',
            status: 'planned',
            priority: 'medium',
            dependencies: '',
            resources: ''
        }];
        updateStep({ timelineItems: newItems });
    };

    const updateTimelineItem = (index, field, value) => {
        const updatedItems = [...(step.timelineItems || [])];
        updatedItems[index] = { ...updatedItems[index], [field]: value };
        updateStep({ timelineItems: updatedItems });
    };

    const deleteTimelineItem = (index) => {
        const updatedItems = (step.timelineItems || []).filter((_, i) => i !== index);
        updateStep({ timelineItems: updatedItems });
    };

    const getPhaseCompletionStatus = (phaseIndex) => {
        switch (phaseIndex) {
            case 0: // Prevention Strategy
                return step.preventionSummary?.trim() && 
                       step.keyChanges?.trim() && 
                       step.expectedOutcome?.trim();
            case 1: // Detection Systems
                return step.detectionMethod?.trim() && 
                       step.monitoringFrequency?.trim() && 
                       step.alertCriteria?.trim();
            case 2: // Implementation Timeline
                return (step.timelineItems || []).length > 0 && 
                       step.implementationDate?.trim() && 
                       step.projectOwner?.trim();
            case 3: // Risk Mitigation
                return step.riskMitigationPlan?.trim() && 
                       step.contingencyPlans?.trim() && 
                       step.riskMonitoring?.trim();
            case 4: // Monitoring & Control
                return step.monitoringPlan?.trim() && 
                       step.controlMechanisms?.trim() && 
                       step.reportingStructure?.trim();
            case 5: // Effectiveness Review
                return step.successMeasures?.trim() && 
                       step.reviewSchedule?.trim() && 
                       step.improvementPlan?.trim();
            default:
                return false;
        }
    };

    // Overall form validation function
    const isFormValid = () => {
        return getPhaseCompletionStatus(0) && 
               getPhaseCompletionStatus(1) && 
               getPhaseCompletionStatus(2) && 
               getPhaseCompletionStatus(3) && 
               getPhaseCompletionStatus(4) && 
               getPhaseCompletionStatus(5);
    };

    const getOverallProgress = () => {
        const actions = step.preventionActions || [];
        if (actions.length === 0) return 0;
        const totalProgress = actions.reduce((sum, action) => sum + (action.progress || 0), 0);
        return Math.round(totalProgress / actions.length);
    };

    const renderPreventionStrategy = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <ShieldIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Prevention Strategy & Planning
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Comprehensive strategy development
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(0) && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(0) ? "success" : "info"} sx={{ mb: 3 }}>
                Define comprehensive prevention strategy to ensure this deviation never recurs.
                {!getPhaseCompletionStatus(0) && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Prevention summary, key changes, and expected outcomes.
                    </Typography>
                )}
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            label="Prevention Strategy Summary"
                            value={step.preventionSummary || ''}
                            onChange={isReadOnly ? undefined : (e) => updateStep({ preventionSummary: e.target.value })}
                            InputProps={{ readOnly: isReadOnly }}
                            placeholder="Comprehensive strategy to prevent this deviation from recurring. Include methodology, approach, and overall prevention philosophy..."
                            sx={{ 
                                mb: 3,
                                '& .MuiOutlinedInput-root': { 
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                }
                            }}
                        />
                    </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Key Changes & Modifications"
                        value={step.keyChanges || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ keyChanges: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Specific changes to processes, procedures, systems, training, etc..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Expected Outcomes & Benefits"
                        value={step.expectedOutcome || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ expectedOutcome: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="What results do we expect from these prevention measures..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Prevention Scope & Boundaries"
                        value={step.preventionScope || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ preventionScope: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define scope of prevention efforts, what's included/excluded..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Resource Requirements"
                        value={step.resourceRequirements || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ resourceRequirements: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Personnel, budget, equipment, time needed for prevention..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderDetectionSystems = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#ff9800', 0.1)}, ${alpha('#ff9800', 0.05)})`,
                        border: `1px solid ${alpha('#ff9800', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <VisibilityIcon sx={{ fontSize: 24, color: '#ff9800' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Early Detection & Warning Systems
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Detection and monitoring systems
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(1) && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(1) ? "success" : "warning"} sx={{ mb: 3 }}>
                Establish robust early detection systems to catch potential issues before they become deviations.
                {!getPhaseCompletionStatus(1) && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Detection methods, monitoring frequency, and alert criteria.
                    </Typography>
                )}
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            multiline
                            rows={5}
                            label="Detection Methods & Technologies"
                        value={step.detectionMethod || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ detectionMethod: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Specific detection methods, technologies, sensors, testing protocols..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Monitoring Frequency & Schedule"
                        value={step.monitoringFrequency || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringFrequency: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How often monitoring occurs, schedules, automated vs manual..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Alert Criteria & Thresholds"
                        value={step.alertCriteria || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ alertCriteria: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="When alerts are triggered, threshold values, escalation criteria..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Response Procedures"
                        value={step.responseProcedures || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ responseProcedures: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="What happens when detection triggers, who responds, how quickly..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderImplementationTimeline = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#4caf50', 0.1)}, ${alpha('#4caf50', 0.05)})`,
                        border: `1px solid ${alpha('#4caf50', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TimelineIcon sx={{ fontSize: 24, color: '#4caf50' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Implementation Timeline & Scheduling
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Project timeline and milestones
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(2) && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(2) ? "success" : "info"} sx={{ mb: 3 }}>
                Create detailed timeline for implementing all prevention measures.
                {!getPhaseCompletionStatus(2) && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Timeline items, implementation date, and project owner.
                    </Typography>
                )}
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                Overall Prevention Progress: {getOverallProgress()}%
                            </Typography>
                        <LinearProgress 
                            variant="determinate" 
                            value={getOverallProgress()} 
                            sx={{ height: 10, borderRadius: 5 }}
                        />
                    </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Target Implementation Date"
                            value={step.implementationDate ? dayjs(step.implementationDate) : null}
                            onChange={isReadOnly ? undefined : (newValue) => updateStep({ 
                                implementationDate: newValue ? newValue.format('YYYY-MM-DD') : null 
                            })}
                            disabled={isReadOnly}
                            format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { 
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': { 
                                            borderRadius: 2,
                                            '& fieldset': {
                                                borderColor: '#000000',
                                                borderWidth: 2
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#000000',
                                                borderWidth: 2
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#000000',
                                                borderWidth: 2
                                            }
                                        }
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Project Owner/Manager"
                        value={step.projectOwner || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ projectOwner: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Person responsible for overall prevention implementation"
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <Accordion sx={{
                        border: `1px solid ${theme.palette.grey[400]}`,
                        borderRadius: 2,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 }
                    }}>
                        <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                borderRadius: 2,
                                '&.Mui-expanded': { minHeight: 48 },
                                '& .MuiAccordionSummary-content': { margin: '12px 0' }
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                Implementation Timeline ({(step.timelineItems || []).length} items)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {(step.timelineItems || []).map((item, index) => (
                                <Paper key={item.id} sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    border: `1px solid ${theme.palette.grey[400]}`, 
                                    borderRadius: 2, 
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                                }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={8}>
                                            <TextField
                                                fullWidth
                                                label="Timeline Item/Task"
                                                value={item.task}
                                                onChange={isReadOnly ? undefined : (e) => updateTimelineItem(index, 'task', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="What needs to be done"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Assigned Owner"
                                                value={item.owner}
                                                onChange={isReadOnly ? undefined : (e) => updateTimelineItem(index, 'owner', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Who is responsible"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Task Description"
                                                value={item.description}
                                                onChange={isReadOnly ? undefined : (e) => updateTimelineItem(index, 'description', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Detailed description of timeline item..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={3}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Start Date"
                                                    value={item.startDate ? dayjs(item.startDate) : null}
                                                    onChange={isReadOnly ? undefined : (newValue) => updateTimelineItem(index, 'startDate', 
                                                        newValue ? newValue.format('YYYY-MM-DD') : null)}
                                                    disabled={isReadOnly}
                                                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            sx: { 
                                                                mb: 2,
                                                                '& .MuiOutlinedInput-root': { 
                                                                    borderRadius: 2,
                                                                    '& fieldset': {
                                                                        borderColor: '#000000',
                                                                        borderWidth: 2
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#000000',
                                                                        borderWidth: 2
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#000000',
                                                                        borderWidth: 2
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="End Date"
                                                    value={item.endDate ? dayjs(item.endDate) : null}
                                                    onChange={isReadOnly ? undefined : (newValue) => updateTimelineItem(index, 'endDate', 
                                                        newValue ? newValue.format('YYYY-MM-DD') : null)}
                                                    disabled={isReadOnly}
                                                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            sx: { 
                                                                mb: 2,
                                                                '& .MuiOutlinedInput-root': { 
                                                                    borderRadius: 2,
                                                                    '& fieldset': {
                                                                        borderColor: '#000000',
                                                                        borderWidth: 2
                                                                    },
                                                                    '&:hover fieldset': {
                                                                        borderColor: '#000000',
                                                                        borderWidth: 2
                                                                    },
                                                                    '&.Mui-focused fieldset': {
                                                                        borderColor: '#000000',
                                                                        borderWidth: 2
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth sx={{ 
                                                mb: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[400],
                                    borderWidth: 1
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[600],
                                    borderWidth: 1
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[700],
                                    borderWidth: 1
                                }
                                            }}>
                                                <InputLabel>Priority</InputLabel>
                                                <Select
                                                    value={item.priority}
                                                    onChange={isReadOnly ? undefined : (e) => updateTimelineItem(index, 'priority', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Priority"
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <MenuItem value="critical">Critical</MenuItem>
                                                    <MenuItem value="high">High</MenuItem>
                                                    <MenuItem value="medium">Medium</MenuItem>
                                                    <MenuItem value="low">Low</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth sx={{ 
                                                mb: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[400],
                                    borderWidth: 1
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[600],
                                    borderWidth: 1
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[700],
                                    borderWidth: 1
                                }
                                            }}>
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                    value={item.status}
                                                    onChange={isReadOnly ? undefined : (e) => updateTimelineItem(index, 'status', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Status"
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <MenuItem value="planned">Planned</MenuItem>
                                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                                    <MenuItem value="completed">Completed</MenuItem>
                                                    <MenuItem value="delayed">Delayed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<TrashIcon />}
                                                    onClick={isReadOnly ? undefined : () => deleteTimelineItem(index)}
                                                    disabled={isReadOnly}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        px: 3,
                                                        opacity: isReadOnly ? 0.6 : 1
                                                    }}
                                                >
                                                    Delete Item
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : addTimelineItem}
                                disabled={isReadOnly}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1,
                                    opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            >
                                Add Timeline Item
                            </Button>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderRiskMitigation = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                        border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <SecurityIcon sx={{ fontSize: 24, color: '#9c27b0' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Risk Mitigation & Contingency Planning
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Risk assessment and backup plans
                        </Typography>
                    </Box>
                    {getPhaseCompletionStatus(3) && (
                        <Chip 
                            label="COMPLETED" 
                            color="success" 
                            sx={{ fontWeight: 500 }}
                            icon={<CheckIcon />}
                        />
                    )}
                </Box>
            
            <Alert severity={getPhaseCompletionStatus(3) ? "success" : "info"} sx={{ mb: 3 }}>
                Identify and mitigate risks that could prevent effective prevention or cause similar deviations.
                {!getPhaseCompletionStatus(3) && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Risk mitigation plan, contingency plans, and risk monitoring.
                    </Typography>
                )}
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Risk Mitigation Plan"
                        value={step.riskMitigationPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ riskMitigationPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Identify risks to prevention effectiveness and mitigation strategies..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Contingency Plans"
                        value={step.contingencyPlans || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ contingencyPlans: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Backup plans if primary prevention measures fail or are insufficient..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Risk Monitoring & Assessment"
                        value={step.riskMonitoring || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ riskMonitoring: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How risks will be continuously monitored and assessed..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Risk Communication Plan"
                        value={step.riskCommunication || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ riskCommunication: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How risk information will be communicated to stakeholders..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderMonitoringControl = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#f44336', 0.1)}, ${alpha('#f44336', 0.05)})`,
                        border: `1px solid ${alpha('#f44336', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TrendingIcon sx={{ fontSize: 24, color: '#f44336' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Ongoing Monitoring & Control Systems
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Establish monitoring and control mechanisms
                        </Typography>
                    </Box>
                {getPhaseCompletionStatus(4) && (
                    <Chip 
                        label="COMPLETED" 
                        color="success" 
                        sx={{ fontWeight: 500 }}
                        icon={<CheckIcon />}
                    />
                )}
            </Box>
            
            <Alert severity={getPhaseCompletionStatus(4) ? "success" : "warning"} sx={{ mb: 3 }}>
                Establish continuous monitoring and control systems to ensure sustained prevention effectiveness.
                {!getPhaseCompletionStatus(4) && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Monitoring plan, control mechanisms, and reporting structure.
                    </Typography>
                )}
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Monitoring Plan & KPIs"
                        value={step.monitoringPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Ongoing monitoring approach, KPIs, metrics, data collection..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Control Mechanisms"
                        value={step.controlMechanisms || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ controlMechanisms: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Process controls, checkpoints, automated systems, manual reviews..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Reporting Structure & Dashboards"
                        value={step.reportingStructure || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ reportingStructure: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How monitoring data is reported, to whom, frequency..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Escalation Procedures"
                        value={step.escalationProcedures || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ escalationProcedures: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="When and how issues are escalated, decision authorities..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderEffectivenessReview = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#607d8b', 0.1)}, ${alpha('#607d8b', 0.05)})`,
                        border: `1px solid ${alpha('#607d8b', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AssessmentIcon sx={{ fontSize: 24, color: '#607d8b' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Effectiveness Review & Continuous Improvement
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Ensure compliance and long-term sustainability
                        </Typography>
                    </Box>
                {getPhaseCompletionStatus(5) && (
                    <Chip 
                        label="COMPLETED" 
                        color="success" 
                        sx={{ fontWeight: 500 }}
                        icon={<CheckIcon />}
                    />
                )}
            </Box>
            
            <Alert severity={getPhaseCompletionStatus(5) ? "success" : "info"} sx={{ mb: 3 }}>
                Establish systematic review and continuous improvement of prevention effectiveness.
                {!getPhaseCompletionStatus(5) && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Success measures, review schedule, and improvement plan.
                    </Typography>
                )}
            </Alert>

                <Grid container spacing={4} sx={{ flexGrow: 1 }}>
                    <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Success Measures & Metrics"
                        value={step.successMeasures || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ successMeasures: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How success will be measured, specific metrics, targets..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Review Schedule & Process"
                        value={step.reviewSchedule || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ reviewSchedule: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="When reviews occur, who participates, review process..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Continuous Improvement Plan"
                        value={step.improvementPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ improvementPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="How prevention measures will be continuously improved..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Lessons Learned Documentation"
                        value={step.lessonsLearned || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ lessonsLearned: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Key lessons learned and knowledge to be preserved..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderPhase = () => {
        switch (activePhase) {
            case 0: return renderPreventionStrategy();
            case 1: return renderDetectionSystems();
            case 2: return renderImplementationTimeline();
            case 3: return renderRiskMitigation();
            case 4: return renderMonitoringControl();
            case 5: return renderEffectivenessReview();
            default: return null;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <ShieldIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D7: Prevent Recurrence
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Implement comprehensive prevention strategies following industry best practices to ensure this deviation never occurs again.
                        </Typography>
                    </Box>
                </Box>
                
                <Stepper 
                    activeStep={-1}
                    sx={{ 
                        mb: 4,
                        '& .MuiStep-root': {
                            opacity: 1,
                            transition: 'opacity 0.3s ease'
                        },
                        '& .MuiStepLabel-root': {
                            cursor: 'pointer',
                            opacity: 0.4,
                            '&[data-active="true"]': {
                                opacity: 1
                            },
                            transition: 'opacity 0.3s ease'
                        },
                        '& .MuiStepIcon-root': {
                            fontSize: '1.5rem',
                            color: theme.palette.grey[400],
                            '&.Mui-active': {
                                color: theme.palette.primary.main,
                            },
                            '&.Mui-completed': {
                                color: theme.palette.grey[400],
                            }
                        },
                        '& .MuiStepIcon-text': {
                            fill: 'white',
                            fontWeight: 500
                        }
                    }} 
                    alternativeLabel
                >
                    {preventionPhases.map((phase, index) => (
                        <Step key={phase.name}>
                            <StepLabel 
                                onClick={() => setActivePhase(index)}
                                data-active={activePhase === index}
                                StepIconProps={{
                                    icon: index + 1
                                }}
                                sx={{
                                    '& .MuiStepIcon-root': {
                                        color: activePhase === index ? theme.palette.primary.main : theme.palette.grey[400],
                                        '&.Mui-completed': {
                                            color: theme.palette.grey[400],
                                        }
                                    }
                                }}
                            >
                                {phase.name}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderPhase()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button 
                        disabled={activePhase === 0}
                        onClick={() => setActivePhase(prev => prev - 1)}
                        variant="outlined"
                        sx={{ 
                            borderRadius: 2, 
                            px: 3,
                            fontWeight: 500,
                            textTransform: 'none',
                            borderColor: theme.palette.grey[400],
                            color: theme.palette.text.primary,
                            '&:hover': {
                                borderColor: theme.palette.grey[600],
                                backgroundColor: alpha(theme.palette.grey[600], 0.04)
                            },
                            '&:disabled': {
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[400]
                            }
                        }}
                    >
                        Previous Phase
                    </Button>
                    <Button 
                        disabled={activePhase === preventionPhases.length - 1}
                        onClick={() => setActivePhase(prev => prev + 1)}
                        variant="contained"
                        sx={{ 
                            borderRadius: 2, 
                            px: 3,
                            fontWeight: 500,
                            textTransform: 'none',
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark
                            },
                            '&:disabled': {
                                backgroundColor: theme.palette.grey[300],
                                color: theme.palette.grey[500]
                            }
                        }}
                    >
                        Next Phase
                    </Button>
                </Box>
                
                <Accordion sx={{
                    mt: 8,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0, marginTop: 8 }
                }}>
                    <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            borderRadius: 2,
                            '&.Mui-expanded': { minHeight: 48 },
                            '& .MuiAccordionSummary-content': { margin: '12px 0' }
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mt: 1}}>
                            Prevention Actions Tracking ({(step.preventionActions || []).length} actions)
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {(step.preventionActions || []).map((action, index) => (
                            <Paper key={action.id} sx={{ 
                                p: 3, 
                                mb: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 2, 
                                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                            }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={8}>
                                            <TextField
                                                fullWidth
                                                label="Prevention Action Title"
                                                value={action.title}
                                                onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'title', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Brief title for prevention action"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControl fullWidth sx={{ 
                                                mb: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[400],
                                    borderWidth: 1
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[600],
                                    borderWidth: 1
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[700],
                                    borderWidth: 1
                                }
                                            }}>
                                                <InputLabel>Prevention Phase</InputLabel>
                                                <Select
                                                    value={action.phase || ''}
                                                    onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'phase', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Prevention Phase"
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <MenuItem value="strategy">Strategy</MenuItem>
                                                    <MenuItem value="detection">Detection</MenuItem>
                                                    <MenuItem value="timeline">Timeline</MenuItem>
                                                    <MenuItem value="risk">Risk Mitigation</MenuItem>
                                                    <MenuItem value="monitoring">Monitoring</MenuItem>
                                                    <MenuItem value="review">Review</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    
                                                                            <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Action Description"
                                                value={action.description}
                                                onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'description', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Detailed description of prevention action..."
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Action Owner"
                                                value={action.owner}
                                                onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'owner', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Person responsible"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Department"
                                                value={action.department}
                                                onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'department', e.target.value)}
                                                InputProps={{ readOnly: isReadOnly }}
                                                placeholder="Department name"
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth sx={{ 
                                                mb: 2,
                                                                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[400],
                                    borderWidth: 1
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[600],
                                    borderWidth: 1
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.grey[700],
                                    borderWidth: 1
                                }
                                            }}>
                                                <InputLabel>Status</InputLabel>
                                                <Select
                                                    value={action.status}
                                                    onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'status', e.target.value)}
                                                    inputProps={{ readOnly: isReadOnly }}
                                                    label="Status"
                                                    sx={{ borderRadius: 2 }}
                                                >
                                                    <MenuItem value="planned">Planned</MenuItem>
                                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                                    <MenuItem value="completed">Completed</MenuItem>
                                                    <MenuItem value="delayed">Delayed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                fullWidth
                                                label="Progress (%)"
                                                type="number"
                                                value={action.progress || ''}
                                                onChange={isReadOnly ? undefined : (e) => updatePreventionAction(index, 'progress', parseInt(e.target.value) || 0)}
                                                placeholder="0-100"
                                                InputProps={{ 
                                                    readOnly: isReadOnly,
                                                    inputProps: { min: 0, max: 100 } 
                                                }}
                                                sx={{ 
                                                    mb: 2,
                                                    '& .MuiOutlinedInput-root': { 
                                                        borderRadius: 2,
                                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<TrashIcon />}
                                                    onClick={isReadOnly ? undefined : () => deletePreventionAction(index)}
                                                    disabled={isReadOnly}
                                                    sx={{ 
                                                        borderRadius: 2, 
                                                        px: 3,
                                                        opacity: isReadOnly ? 0.6 : 1
                                                    }}
                                                >
                                                    Delete Action
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                            </Paper>
                        ))}
                        
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : addPreventionAction}
                                disabled={isReadOnly}
                                sx={{ 
                                    mt: 2,
                                    borderRadius: 2,
                                    fontWeight: 500,
                                    px: 3,
                                    py: 1,
                                    opacity: isReadOnly ? 0.6 : 1,
                                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                                }}
                            >
                                Add Prevention Action
                            </Button>
                    </AccordionDetails>
                </Accordion>
                
                <Paper sx={{ 
                    p: 3, 
                    mt: 4, 
                    backgroundColor: '#fff', 
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`
                }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={step.completed || false}
                                onChange={isReadOnly ? undefined : (e) => {
                                    if (!isFormValid() && e.target.checked) {
                                        alert('Please complete all prevention phases before marking as complete.');
                                        return;
                                    }
                                    updateStep({ completed: e.target.checked });
                                }}
                                disabled={isReadOnly || !isFormValid()}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 28, color: isFormValid() ? '#2196f3' : '#ccc' } }}
                            />
                        }
                        label=""
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: isFormValid() ? '#0277bd' : '#666', display: 'inline' }}>
                        D7 Prevention Complete - Comprehensive Prevention Strategy Implemented
                    </Typography>
                    {!isFormValid() && (
                        <Typography variant="body2" sx={{ color: '#f44336', fontStyle: 'italic', display: 'block', mt: 1 }}>
                            (Complete all phases: Strategy, Detection, Timeline, Risk Mitigation, Monitoring, and Review)
                        </Typography>
                    )}
                </Paper>
            </Box>
        </LocalizationProvider>
    );
} 